<template>
  <section class="mt-10 md:mt-0 w-full px-4 lg:px-0">
    <div class="flex justify-center w-full max-w-7xl mx-auto">
      <!-- Flex container for images and product name -->
      <div
        class="flex flex-row items-center justify-center w-full md:justify-between gap-6"
      >
        <!-- Left Image -->
        <div 
          class="w-24 md:w-1/4 lg:w-1/3 hidden md:block" 
        >
          <img v-if="!errorState.left"
            ref="leftImage"
            class="w-full"
            :src="leftImageSrc"
            :alt="`${formattedProductType} Left Image`"
          />
          <div v-else class="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
              <rect width="100" height="100" fill="#f3f4f6"/>
              <text x="50" y="50" font-family="Arial" font-size="12" text-anchor="middle" dominant-baseline="middle" fill="#666">{{ productType.replace('insurance', '') }}</text>
            </svg>
          </div>
        </div>

        <!-- CTA -->
        <div class="flex flex-col items-center">
          <h2
            class="text-center mb-4 md:mb-8 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 whitespace-nowrap"
          >
            {{ formattedProductType }}
          </h2>

          <UIButton
            label="GET A QUOTE"
            variant="outlined"
            severity="danger"
            size="large"
            @click="navigateToChooseProducts"
          ></UIButton>
        </div>

        <!-- Right Image -->
        <div 
          class="w-24 md:w-1/4 lg:w-1/3 hidden md:block"
        >
          <img v-if="!errorState.right"
            ref="rightImage"
            class="w-full"
            :src="rightImageSrc"
            :alt="`${formattedProductType} Right Image`"
          />
          <div v-else class="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
              <rect width="100" height="100" fill="#f3f4f6"/>
              <text x="50" y="50" font-family="Arial" font-size="12" text-anchor="middle" dominant-baseline="middle" fill="#666">Insurance</text>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { UIButton } from '@/components/ui';
import { getStandardizedProductType } from '@/utils/imageUtils';

// Import all possible product hero images from various locations to cover all bases
const productImages = import.meta.glob([
  '@/assets/images/products/hero/**/*.{svg,png}',
  '@/assets/images/productHeroBanners/**/*.{svg,png}',
  '@/assets/images/banners/**/*.{svg,png}'
], { eager: true });

// Add console logging to debug available images
// console.log('Available product images:', Object.keys(productImages));

export default {
  components: {
    UIButton,
  },
  props: {
    productType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      errorState: {
        left: false,
        right: false
      }
    };
  },
  mounted() {
    // Log the computed image paths for debugging
    console.log('Left image path:', this.leftImageSrc);
    console.log('Right image path:', this.rightImageSrc);
  },
  computed: {
    formattedProductType() {
      const mapping = {
        homeinsurance: 'Home Insurance',
        autoinsurance: 'Auto Insurance',
        floodinsurance: 'Flood Insurance',
        petinsurance: 'Pet Insurance',
        lifeinsurance: 'Life Insurance',
        medicare: 'Medicare',
        commercialinsurance: 'Commercial Insurance',
        umbrellainsurance: 'Umbrella Insurance',
        businessinsurance: 'Business Insurance',
      };
      return mapping[this.productType] || this.productType;
    },
    standardizedProductType() {
      return getStandardizedProductType(this.productType);
    },
    leftImageSrc() {
      // Try to find the left image with multiple path patterns
      const productType = this.standardizedProductType;
      const rawProductType = this.productType;
      
      // Array of possible path patterns
      const possiblePaths = [
        // Products/hero pattern
        `/resources/js/assets/images/products/hero/${productType}/hero-${productType}-left.svg`,
        `/resources/js/assets/images/products/hero/${productType}/hero-${productType}-left.png`,
        
        // ProductHeroBanners pattern
        `/resources/js/assets/images/productHeroBanners/${rawProductType}/${rawProductType}HeroBannerLeft.svg`,
        `/resources/js/assets/images/productHeroBanners/${rawProductType}/${rawProductType}HeroBannerLeft.png`,
        
        // Banners pattern
        `/resources/js/assets/images/banners/${rawProductType}/${rawProductType}HeroBannerLeft.svg`,
        `/resources/js/assets/images/banners/${rawProductType}/${rawProductType}HeroBannerLeft.png`
      ];
      
      // Try each path
      for (const path of possiblePaths) {
        if (productImages[path]) {
          console.log('Found left image at:', path);
          return productImages[path].default;
        }
      }
      
      // If no match, set error state and return empty string
      console.error('Left image not found for', rawProductType);
      this.errorState.left = true;
      return '';
    },
    rightImageSrc() {
      // Try to find the right image with multiple path patterns
      const productType = this.standardizedProductType;
      const rawProductType = this.productType;
      
      // Array of possible path patterns
      const possiblePaths = [
        // Products/hero pattern
        `/resources/js/assets/images/products/hero/${productType}/hero-${productType}-right.svg`,
        `/resources/js/assets/images/products/hero/${productType}/hero-${productType}-right.png`,
        
        // ProductHeroBanners pattern
        `/resources/js/assets/images/productHeroBanners/${rawProductType}/${rawProductType}HeroBannerRight.svg`,
        `/resources/js/assets/images/productHeroBanners/${rawProductType}/${rawProductType}HeroBannerRight.png`,
        
        // Banners pattern
        `/resources/js/assets/images/banners/${rawProductType}/${rawProductType}HeroBannerRight.svg`,
        `/resources/js/assets/images/banners/${rawProductType}/${rawProductType}HeroBannerRight.png`
      ];
      
      // Try each path
      for (const path of possiblePaths) {
        if (productImages[path]) {
          console.log('Found right image at:', path);
          return productImages[path].default;
        }
      }
      
      // If no match, set error state and return empty string
      console.error('Right image not found for', rawProductType);
      this.errorState.right = true;
      return '';
    },
  },
  methods: {
    navigateToChooseProducts() {
      this.$router.push({
        path: `/quotes/${this.productType}`,
        query: { productType: this.formattedProductType },
      });
    },
  },
};
</script>
